import { formatEncoded } from "@e308/ux";
import { BUILDINGS } from "../content/buildings.js";
import { TECHNOLOGIES } from "../content/research.js";
import { microFromString } from "../exact-numbers.js";
import { UNIT_IDS } from "../logistics-types.js";
import { researchLegal } from "../research-state.js";
import type { SeatObservation } from "../types.js";

export interface Guidance {
  readonly id: string;
  readonly message: string;
  readonly tab: "Borders" | "Town" | "Research";
  readonly focus: string;
  readonly borderId?: string;
}

/** Current authoritative volley reports, not queued orders or stale damage summaries. */
export function undefendedAttacks(o: SeatObservation): Guidance[] {
  if (!o.self.alive || o.match.status === "ended") return [];
  return o.seats.flatMap((enemy) => {
    const position = o.self.logistics.borders[enemy.id];
    const report = enemy.combat.borders[o.self.id]?.lastAttack;
    if (!position || !enemy.alive || UNIT_IDS.some((id) => positive(position.troops[id])))
      return [];
    if (!report || ![report.infrastructureDamage, report.shieldAbsorbed].some(positive)) return [];
    return [
      {
        id: `undefended-${enemy.id}`,
        message: `⚠ ${enemy.name}: ${BUILDINGS.find((building) => building.id === report.effectiveTarget)?.name ?? "HQ"} under attack. No defenders · ${formatEncoded(o.self.units["guard-robot"], { significantDigits: 3 })} Guards undeployed →`,
        tab: "Borders" as const,
        focus: `army-share-${enemy.id}:control`,
        borderId: enemy.id,
      },
    ];
  });
}

export function advisorCandidates(o: SeatObservation): Guidance[] {
  const { self } = o;
  const tips: Guidance[] = [];
  const border = Object.entries(self.logistics.borders).find(
    ([id, position]) =>
      !UNIT_IDS.some((unit) => positive(position.troops[unit])) &&
      !self.logistics.transits.some(
        (transit) => transit.destination === id || transit.finalDestination === id,
      ),
  );
  if (border && positive(self.units["guard-robot"]))
    tips.push({
      id: "reserves",
      tab: "Borders",
      focus: `army-share-${border[0]}:control`,
      borderId: border[0],
      message:
        "Your Guards are still on the bench. Deploy some to each exposed border before trouble arrives. Undeployed reserves protect neither mines nor Headquarters; travel takes game time.",
    });
  const generatorQueued =
    self.manufacturing.construction.some((order) => order.target === "power-generator") &&
    !self.manufacturing.paused.construction;
  if (
    !generatorQueued &&
    Object.values(self.economy.power).some(
      (lane) => microFromString(lane.received) < microFromString(lane.requested),
    )
  )
    tips.push({
      id: "power",
      tab: "Town",
      focus: "construction-target:control",
      message:
        "Power is falling short. Consider a Power Generator, or lower less important departments’ operation. Check requested versus received Power before expanding again.",
    });
  const research = self.research;
  if (
    !research.active &&
    research.queue.length === 0 &&
    TECHNOLOGIES.some(
      (tech) =>
        researchLegal(research, { id: "advisor", kind: "technology", target: tech.id }) &&
        microFromString(self.researchPoints) >= microFromString(String(tech.rpCost)),
    )
  )
    tips.push({
      id: "research",
      tab: "Research",
      focus: "research-heading",
      message:
        "Your research desk is empty, and you can afford an available technology. Pick an upgrade that supports your plan—those RP won’t spend themselves.",
    });
  if (
    !self.manufacturing.paused.construction &&
    self.manufacturing.construction.length === 0 &&
    self.constructionSplit.newConstruction > 0 &&
    positive(self.metal)
  )
    tips.push({
      id: "construction",
      tab: "Town",
      focus: "construction-target:control",
      message:
        "Construction is idle. If you aren’t deliberately saving Metal, queue your next building; unused Construction Work cannot be stored.",
    });
  return tips;
}

/** Presentation-only memory: bounded by the small candidate set, never part of a save. */
export class PracticeAdvisor {
  #since = new Map<string, number>();
  #dismissed = new Set<string>();
  #lastTime = -1;
  dismiss(id: string): void {
    if (this.#since.has(id)) this.#dismissed.add(id);
  }
  reset(): void {
    this.#since.clear();
    this.#dismissed.clear();
    this.#lastTime = -1;
  }
  update(o: SeatObservation, enabled: boolean): Guidance | undefined {
    if (
      !enabled ||
      !o.self.alive ||
      o.match.status === "ended" ||
      o.match.mode !== "private-practice"
    ) {
      this.reset();
      return undefined;
    }
    if (o.gameTimeMs < this.#lastTime) this.reset();
    this.#lastTime = o.gameTimeMs;
    const candidates = advisorCandidates(o);
    const ids = new Set(candidates.map((tip) => tip.id));
    for (const id of this.#since.keys())
      if (!ids.has(id)) {
        this.#since.delete(id);
        this.#dismissed.delete(id);
      }
    for (const tip of candidates)
      if (!this.#since.has(tip.id)) this.#since.set(tip.id, o.gameTimeMs);
    return candidates.find(
      (tip) =>
        !this.#dismissed.has(tip.id) &&
        o.gameTimeMs - (this.#since.get(tip.id) ?? o.gameTimeMs) >= 10_000,
    );
  }
}

function positive(value: string): boolean {
  return microFromString(value) > 0n;
}
